import * as React from 'react'
import type { FileId, FileName } from '../../../shared/domain/files/types.no-deps'
import type { CreateAnswer } from '../../../shared/domain/flows/answer.no-deps'
import {
    type Answer,
    type ElementId,
    photoFileNameToFileId,
    type SignatureAnswer,
} from '../../../shared/domain/flows/types.no-deps'
import type { ClientLogger } from '../../../shared/lib/ClientLogger.no-deps'
import type { Dictionary } from '../../../shared/lib/functional/dictionary.no-deps'
import type { CommonDeps } from '../base/CommonDeps'
import { renderCatch } from '../error/renderCatch'
import { ExecutionElement } from '../flow/ExecutionElement'
import { SignaturePad } from '../ui/SignaturePad'
import type { FileUploadData } from '../upload/FileUploadData'
import type { Profile } from '../user/Profile'
import { ElementLabel } from './ElementLabel'
import { type FileUrlStates, withFileUrl } from '../file/FileUrlManager'
import i18next from 'i18next'
import type { PromiseThunkQueue } from '../../../shared/lib/PromiseThunkQueue.no-deps'
import type { LoopPathIds } from '../../../shared/domain/flows/LoopPath.no-deps'

interface Props {
    deps: CommonDeps
    elementId: ElementId
    loopPathIds: LoopPathIds
    label: string
    timelineDetails: {
        hasBeenEdited: boolean
        elementAnswers: Answer[]
        choicesLookup: Dictionary<string> | null
        userProfiles: Profile[]
    } | null
    required: boolean
    answerValue: SignatureAnswer['value']
    validationMessages: string[]
    createAnswer: CreateAnswer<SignatureAnswer>
    saveBlob: (blob: Blob) => Promise<FileUploadData>
    cancelUpload: (fileId: FileId, logger: ClientLogger) => Promise<void>
    fileUrlStates: FileUrlStates
    promiseThunkQueue: PromiseThunkQueue
    openOptions?: () => void
}

interface State {
    canConfirm: boolean
}

export class ElementSignature extends React.Component<Props, State> {
    public render = () => renderCatch(this.props.deps, this.tryRender)

    public state: State = {
        canConfirm: false,
    }

    private createAnswer = (value: FileName) =>
        this.props.createAnswer((base) => ({
            ...base,
            type: 'signature',
            value,
        }))

    private onClear = (clearSignature: () => void) => {
        const { answerValue, deps } = this.props

        if (clearSignature) clearSignature()

        if (this.state.canConfirm) {
            this.setState({ canConfirm: false })
        }

        if (answerValue == null) return

        const logger = deps.createLogger('ExecutionPage.onClear')

        this.props.cancelUpload(this.getFileId(), logger)
        this.createAnswer(null)
    }

    private onStrokeEnd = () => {
        if (this.state.canConfirm) return
        this.setState({ canConfirm: true })
    }

    private onConfirm = async (getSignature: () => Promise<Blob>) => {
        await this.props.promiseThunkQueue.push(async () => {
            const fileMeta = await this.props.saveBlob(await getSignature())
            this.createAnswer(fileMeta.fileName)
            this.setState({ canConfirm: false })
        })
    }

    private getFileId() {
        return photoFileNameToFileId(this.props.answerValue)
    }

    private renderImageContent = () => {
        const { deps, fileUrlStates } = this.props
        const fileId = this.getFileId()

        return (
            <div className="AppCore__flow__ElementSignature">
                <div className="AppCore__flow__ElementSignature__photo">
                    {withFileUrl(deps, fileUrlStates, fileId, (url) => {
                        return url && <img className="AppCore__flow__ElementSignature__photo-img" src={url} />
                    })}
                </div>

                <div className="AppCore__flow__ElementSignature__actions">{this.renderClearButton()}</div>
            </div>
        )
    }

    private renderConfirmButton = (getSignature: () => Promise<Blob>) => {
        const { canConfirm } = this.state
        if (!canConfirm) return null
        return (
            <div className="AppCore__flow__ElementSignature__confirm" onClick={() => this.onConfirm(getSignature)}>
                {i18next.t('appcore:element.signature.confirm')}
            </div>
        )
    }

    private renderClearButton = (clearSignature: () => void = () => null) => {
        return (
            <div className="AppCore__flow__ElementSignature__clear" onClick={() => this.onClear(clearSignature)}>
                {i18next.t('appcore:element.signature.clear')}
            </div>
        )
    }

    private renderInputContent = () => {
        return (
            <SignaturePad onEnd={this.onStrokeEnd}>
                {({ clearSignature, signaturePad, getSignature }) => (
                    <div className="AppCore__flow__ElementSignature">
                        <div className="AppCore__flow__ElementSignature__pad">{signaturePad}</div>

                        <div className="AppCore__flow__ElementSignature__actions">
                            {this.renderClearButton(clearSignature)}
                            {this.renderConfirmButton(getSignature)}
                        </div>
                    </div>
                )}
            </SignaturePad>
        )
    }

    private tryRender = () => {
        const props = this.props
        const { deps, elementId, label, required, validationMessages } = props
        const hasFile = Boolean(props.answerValue)

        return (
            <ExecutionElement
                deps={deps}
                elementId={elementId}
                loopPathIds={this.props.loopPathIds}
                validationMessages={validationMessages}
                timelineDetails={props.timelineDetails}
            >
                <ElementLabel
                    required={required}
                    label={label}
                    hasBeenEdited={props.timelineDetails && props.timelineDetails.hasBeenEdited}
                    language={deps.language}
                    validationMessages={validationMessages}
                    openOptions={this.props.openOptions}
                />
                {hasFile ? this.renderImageContent() : this.renderInputContent()}
            </ExecutionElement>
        )
    }
}
